import type { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, useState, type RefObject } from 'react';

import { splitFileSystemNodes } from '../../helpers/knowledge-base/fileSystemNode.js';
import type { FileSystemNode, FolderNode } from '../../types/fileSystem.js';
import type { RagTableType } from '../../types/rag.js';

type Params = {
  folders: FolderNode[];
  gridRef: RefObject<AgGridReact | null>;
};

export const useKnowledgeBaseSelection = ({ folders, gridRef }: Params) => {
  const [rows, setRows] = useState<RagTableType[]>([]);
  const [folderIds, setFolderIds] = useState<string[]>([]);

  const selectedFolders = useMemo(
    () => folders.filter((folder) => folderIds.includes(folder.id)),
    [folders, folderIds],
  );

  const nodes = useMemo<FileSystemNode[]>(
    () => [
      ...selectedFolders,
      ...rows.map((row) => ({ kind: 'file' as const, id: row.fileId, ...row })),
    ],
    [selectedFolders, rows],
  );

  const selectNodes = useCallback((selected: FileSystemNode[]) => {
    const picked = splitFileSystemNodes(selected);

    setRows(picked.files);
    setFolderIds(picked.folders.map((folder) => folder.id));
  }, []);

  const clear = useCallback(() => {
    setRows([]);
    setFolderIds([]);
    gridRef.current?.api?.deselectAll();
  }, [gridRef]);

  return {
    rows,
    folders: selectedFolders,
    nodes,
    count: rows.length + folderIds.length,
    selectNodes,
    /** The flat table only ever holds files. */
    selectRows: setRows,
    clear,
  };
};
